import { useCallback, useContext, useEffect, useMemo, useReducer, useRef } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import {
  createEmaProceduralStep,
  getEmaProceduralSteps,
  markEmaProceduralStepsDone
} from '../../api/ema';
import type { CreateEmaProceduralStepRequest } from '../../types/dtos/ema';
import GlobalStore from '../../store';
import GlobalActions from '../../store/actions';
import AuthContext from '../../store/Auth/AuthProvider';
import {
  createInitialState,
  getDocumentUrls,
  isDraftValid,
  proceduralStepsReviewReducer
} from './ProceduralStepsEMAReview.state';
import type {
  ProceduralStepDraftField,
  ProceduralStepsAuthContext,
  ProceduralStepsDialog,
  ProceduralStepsLocationState,
  ProceduralStepsReviewModel,
  ProceduralStepsRouteParams,
  ProceduralStepsStoreContext
} from './ProceduralStepsEMAReview.types';

const useProceduralStepsEMAReview = (): ProceduralStepsReviewModel => {
  const { currentUser } = useContext(AuthContext) as unknown as ProceduralStepsAuthContext;
  const { dispatch: globalDispatch } = useContext(
    GlobalStore
  ) as unknown as ProceduralStepsStoreContext;
  const history = useHistory();
  const location = useLocation<ProceduralStepsLocationState>();
  const {
    productNumber,
    applNo,
    curationId: routeCurationId
  } = useParams<ProceduralStepsRouteParams>();
  const resolvedProductNumber = productNumber || applNo || '';
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const curationId =
    routeCurationId ||
    searchParams.get('curation_id') ||
    searchParams.get('curationId') ||
    location.state?.curation_id ||
    location.state?.curationId;

  const [state, dispatch] = useReducer(proceduralStepsReviewReducer, undefined, createInitialState);
  const mountedRef = useRef(true);
  const requestVersionRef = useRef(0);

  const showAlert = useCallback(
    (message: string, color?: string) => {
      globalDispatch({
        type: GlobalActions.SET_ALERT,
        value: { status: true, message, color }
      });
    },
    [globalDispatch]
  );

  const loadProceduralSteps = useCallback(async () => {
    const requestVersion = requestVersionRef.current + 1;
    requestVersionRef.current = requestVersion;
    dispatch({ type: 'LOAD_STARTED' });

    try {
      const response = await getEmaProceduralSteps(resolvedProductNumber);
      if (!mountedRef.current || requestVersion !== requestVersionRef.current) {
        return;
      }

      const responseBody = response?.data?.body;
      if (response?.status !== 200 || !responseBody) {
        dispatch({ type: 'LOAD_FAILED' });
        showAlert(response?.data?.message || 'Unable to load procedural steps.');
        return;
      }

      const urls = getDocumentUrls(responseBody.results, responseBody.pdf_url);
      dispatch({
        type: 'LOAD_SUCCEEDED',
        value: {
          steps: responseBody.results,
          total: responseBody.total ?? responseBody.results.length,
          ...urls
        }
      });
    } catch {
      if (mountedRef.current && requestVersion === requestVersionRef.current) {
        dispatch({ type: 'LOAD_FAILED' });
        showAlert('Unable to load procedural steps.');
      }
    }
  }, [resolvedProductNumber, showAlert]);

  useEffect(() => {
    mountedRef.current = true;
    loadProceduralSteps();

    return () => {
      mountedRef.current = false;
      requestVersionRef.current += 1;
    };
  }, [loadProceduralSteps]);

  const selectedStep = useMemo(
    () => state.steps.find(step => step.id === state.selectedStepId) || null,
    [state.selectedStepId, state.steps]
  );

  const filteredSteps = useMemo(() => {
    const normalizedQuery = state.query.trim().toLowerCase();
    if (!normalizedQuery) {
      return state.steps;
    }

    return state.steps.filter(step =>
      (step.appln_no || '').toLowerCase().includes(normalizedQuery)
    );
  }, [state.query, state.steps]);

  const setQuery = useCallback((value: string) => {
    dispatch({ type: 'SET_QUERY', value });
  }, []);

  const chooseQuery = useCallback((value: string) => {
    dispatch({ type: 'CHOOSE_QUERY', value });
  }, []);

  const setDropdownOpen = useCallback((value: boolean) => {
    dispatch({ type: 'SET_DROPDOWN', value });
  }, []);

  const search = useCallback(() => {
    const step = state.steps.find(item => item.appln_no === state.query);
    if (step) {
      dispatch({ type: 'SELECT_STEP', value: step });
      return;
    }

    dispatch({ type: 'CLEAR_SELECTION' });
    showAlert('Not found! Please re-check the Application Number or Add a New Application.');
  }, [showAlert, state.query, state.steps]);

  const startAdd = useCallback(() => {
    dispatch({ type: 'START_ADD' });
  }, []);

  const startEdit = useCallback(() => {
    dispatch({ type: 'START_EDIT' });
  }, []);

  const cancelChanges = useCallback(() => {
    if (state.mode === 'edit') {
      showAlert('Your changes are stored temporarily. Please submit to save changes.');
    }
    dispatch({ type: 'CANCEL_CHANGES', resetDraft: state.mode === 'add' });
  }, [showAlert, state.mode]);

  const updateDraft = useCallback((field: ProceduralStepDraftField, value: string) => {
    dispatch({ type: 'UPDATE_DRAFT', field, value });
  }, []);

  const requestSubmit = useCallback(() => {
    if (!curationId) {
      showAlert('A curation ID is required to save procedural steps.');
      return;
    }
    if (!currentUser?.email || !currentUser?.['custom:user']) {
      showAlert('Curator name and email are required to save procedural steps.');
      return;
    }
    if (!isDraftValid(state.draft)) {
      showAlert('Application Number and Scope are required before you save.');
      return;
    }

    const isDuplicate =
      state.mode === 'add' &&
      state.steps.some(step => (step.appln_no || '').trim() === state.draft.applNo.trim());
    dispatch({ type: 'OPEN_DIALOG', value: isDuplicate ? 'duplicate' : 'submit' });
  }, [curationId, currentUser, showAlert, state.draft, state.mode, state.steps]);

  const saveStep = useCallback(async () => {
    if (!curationId || !isDraftValid(state.draft)) {
      return;
    }

    const wasAdd = state.mode === 'add';
    const payload: CreateEmaProceduralStepRequest = {
      appl_no: state.draft.applNo.trim(),
      scope: state.draft.scope.trim(),
      opinion: state.draft.opinion,
      commission_decision: state.draft.commissionDecision.trim() || 'n/a',
      product_info_affected: state.draft.productInfoAffected,
      summary: state.draft.summary,
      sourced_from: state.sourceUrl,
      curator_email: currentUser?.email ?? '',
      curator_name: currentUser?.['custom:user'] ?? ''
    };

    dispatch({ type: 'SET_LOADING', value: true });
    try {
      const response = await createEmaProceduralStep(resolvedProductNumber, curationId, payload);
      if (!mountedRef.current) {
        return;
      }
      if (response?.status !== 200) {
        dispatch({ type: 'SET_LOADING', value: false });
        showAlert(response?.data?.message || 'Unable to save procedural step.');
        return;
      }

      dispatch({ type: 'SAVE_SUCCEEDED', wasAdd });
      showAlert('Saved Successfully!', 'success');
      await loadProceduralSteps();
    } catch {
      if (mountedRef.current) {
        dispatch({ type: 'SET_LOADING', value: false });
        showAlert('Unable to save procedural step.');
      }
    }
  }, [
    curationId,
    currentUser,
    loadProceduralSteps,
    resolvedProductNumber,
    showAlert,
    state.draft,
    state.mode,
    state.sourceUrl
  ]);

  const confirmSubmit = useCallback(() => {
    dispatch({ type: 'CLOSE_DIALOG', value: 'submit' });
    saveStep();
  }, [saveStep]);

  const confirmDuplicate = useCallback(() => {
    dispatch({ type: 'CLOSE_DIALOG', value: 'duplicate' });
    saveStep();
  }, [saveStep]);

  const closeDialog = useCallback((dialog: ProceduralStepsDialog) => {
    dispatch({ type: 'CLOSE_DIALOG', value: dialog });
  }, []);

  const closeSubmitDialog = useCallback(() => {
    closeDialog('submit');
  }, [closeDialog]);

  const closeDuplicateDialog = useCallback(() => {
    closeDialog('duplicate');
    dispatch({ type: 'CANCEL_CHANGES', resetDraft: true });
  }, [closeDialog]);

  const requestDone = useCallback(() => {
    if (curationId) {
      dispatch({ type: 'OPEN_DIALOG', value: 'done' });
    }
  }, [curationId]);

  const confirmDone = useCallback(async () => {
    if (!curationId) {
      return;
    }

    dispatch({ type: 'CLOSE_DIALOG', value: 'done' });
    dispatch({ type: 'SET_LOADING', value: true });
    try {
      const response = await markEmaProceduralStepsDone(resolvedProductNumber, curationId);
      if (!mountedRef.current) {
        return;
      }

      dispatch({ type: 'SET_LOADING', value: false });
      if (response?.status !== 200) {
        showAlert(response?.data?.message || 'Unable to mark Procedural Steps as Done.');
        return;
      }

      showAlert('Procedural Steps marked as Done.', 'success');
      history.push('/ema/curations');
    } catch {
      if (mountedRef.current) {
        dispatch({ type: 'SET_LOADING', value: false });
        showAlert('Unable to mark Procedural Steps as Done.');
      }
    }
  }, [curationId, history, resolvedProductNumber, showAlert]);

  const goBack = useCallback(() => {
    history.push('/ema/curations');
  }, [history]);

  const closeDoneDialog = useCallback(() => {
    closeDialog('done');
  }, [closeDialog]);

  const actions = useMemo(
    () => ({
      setQuery,
      chooseQuery,
      setDropdownOpen,
      search,
      startAdd,
      startEdit,
      cancelChanges,
      updateDraft,
      requestSubmit,
      confirmSubmit,
      confirmDuplicate,
      closeSubmitDialog,
      closeDuplicateDialog,
      requestDone,
      confirmDone,
      closeDoneDialog,
      goBack
    }),
    [
      cancelChanges,
      chooseQuery,
      closeDoneDialog,
      closeDuplicateDialog,
      closeSubmitDialog,
      confirmDone,
      confirmDuplicate,
      confirmSubmit,
      goBack,
      requestDone,
      requestSubmit,
      search,
      setDropdownOpen,
      setQuery,
      startAdd,
      startEdit,
      updateDraft
    ]
  );

  return useMemo(
    () => ({
      productNumber: resolvedProductNumber,
      hasCurationId: Boolean(curationId),
      state,
      selectedStep,
      filteredSteps,
      actions
    }),
    [actions, curationId, filteredSteps, resolvedProductNumber, selectedStep, state]
  );
};

export default useProceduralStepsEMAReview;
